import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Paper,
  Select,
  MenuItem,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { useLocation } from "react-router-dom";
import { useLoader } from "../common/LoaderContext";
import { downloadInsights } from "../services/dashboard.services";

interface DataRow {
  rule_number: string;
  rule_description: string;
  wage_type: string;
  variance: string;
  status: string;
  payscale_type: string;
  personnel_number: string;
  person_id:string;
}

const ViewValidationResult: React.FC = () => {
  const { hideLoader } = useLoader();
  const location = useLocation();
  const rawData = location.state?.validationData?.result || [];
  const docName = location.state?.docName || location.state?.validationData?.doc_name || "Validation_Report";
  const session = location.state?.session || location.state?.validationData?.session || "";

  
  const [showPersonNo,setShowPersonNo] = useState(true);
  const [showPersonId,setShowPersonId] = useState(true);


  const validationData: any[] = Array.isArray(rawData)
    ? rawData.map((item: any) => ({
      ruleNumber: item.rule_number || "-",
      description: item.rule_description || "-",
      wageType: item.wage_type || "-",
      variance: item.variance?.toString() || "-",
      status: item.status || "-",
      payScaleType: item.payscale_type || "-",
      personNo: item.personnel_number || "-",
      personId: item.person_id || "-",
      explanation: item.explanation || "",
    }))
    : [];

  const personNo = useMemo(() => {
    const unique = Array.from(new Set(validationData.map((r) => r.personNo)));
   
    return ["All", ...unique];
  }, [validationData]);
  // if(personNo[1] == "-"){
  //     setShowPersonNo(false);
  // }
  const payScaleTypes = useMemo(() => {
    const unique = Array.from(new Set(validationData.map((r) => r.payScaleType)));
    return ["All", ...unique];
  }, [validationData]);

   const personIds = useMemo(() => {
    const unique = Array.from(new Set(validationData.map((r) => r.personId)));
   
    return ["All", ...unique];
  }, [validationData]);
//  if(personIds[1] == "-"){
//       setShowPersonId(false);
//     }
  const status = useMemo(() => {
    const unique = Array.from(new Set(validationData.map((r) => r.status)));
    return ["All", ...unique];
  }, [validationData]);
  console.log(status);
  // Custom tooltip styling
  const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
      maxWidth: 221,
      fontSize: "12px",
      padding: "8px 12px",
    },
  }));

  const [payScaleTypeFilter, setPayScaleTypeFilter] = useState("All");
  const [personNoFilter, setPersonNoFilter] = useState("All");
  const [personIdFilter, setPersonIdFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filteredData = useMemo(() => {
    return validationData.filter((row) => {
      const matchPayScale =
        payScaleTypeFilter === "All" || row.payScaleType === payScaleTypeFilter;
      const matchPersonNo =
        personNoFilter === "All" || row.personNo === personNoFilter;
      const matchPersonId =
        personIdFilter === "All" || row.personId === personIdFilter;
      const matchStatus =
        statusFilter === "All" || row.status === statusFilter;

      const matchSearch =
        searchText === "" ||
        Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase());
      return matchPayScale && matchPersonNo && matchPersonId && matchStatus && matchSearch;
    });
    
  }, [validationData, payScaleTypeFilter, personNoFilter, personIdFilter,  statusFilter, searchText]);

  const pagedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => setPage(newPage);

  useEffect(() => {
    setPage(0);
  }, [payScaleTypeFilter, personNoFilter, personIdFilter, statusFilter, searchText]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setPayScaleTypeFilter("All");
    setStatusFilter("All");
    setPersonNoFilter("All");
     setPersonIdFilter("All");
    setSearchText("");
    setPage(0);
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadInsights(docName, session);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${docName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Download error:", error.message);
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="page-header-container">
        <div className="left-section">
          <div className="page-heading-section">
            <label className="page-heading">Insights <span style={{fontSize:'12px',fontWeight: 400,fontStyle: 'italic',color: '#BFC1C2'}}>({docName}, Session - {session})</span></label>
            <p className="page-subheading">
              Check validation status and deep dive on rules validated
            </p>
          </div>
        </div>
        <div className="right-section">
          <Button
            className="right-button"
            variant="contained"
            sx={{ textTransform: "none", backgroundColor: "#007CB0" }}
            onClick={handleDownload}
          >
            Download
          </Button>
        </div>
      </div>

      <Box sx={{ padding: "0px 72px 72px 72px" }}>
        <div className="card dark-table">
          <Box
            sx={{
              paddingTop: "32px",
              color: "#fff",
            }}
          >
            {/* Filters */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                {/* Pay Scale Filter */}
                <FormControl
                  sx={{
                    minWidth: 120,
                    "& .MuiInputLabel-root": { color: "#fff" },
                    "& .MuiSelect-select": {
                      color: "#fff",
                      background: "#252628",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                    "& .MuiSelect-icon": { color: "#fff" },
                  }}
                  size="small"
                >
                  <InputLabel>Pay scale type</InputLabel>
                  <Select
                    value={payScaleTypeFilter}
                    label="Pay scale type"
                    onChange={(e) => setPayScaleTypeFilter(e.target.value)}
                  >
                    {payScaleTypes.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Person No Filter */}
                {personNo[1] != "-" && <FormControl
                  sx={{
                    minWidth: 120,
                    "& .MuiInputLabel-root": { color: "#fff" },
                    "& .MuiSelect-select": {
                      color: "#fff",
                      background: "#252628",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                    "& .MuiSelect-icon": { color: "#fff" },
                  }}
                  size="small"
                >
                  <InputLabel>Person No.</InputLabel>
                  <Select
                    value={personNoFilter}
                    label="Person No."
                    onChange={(e) => setPersonNoFilter(e.target.value)}
                  >
                    {personNo.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>}
                {/* Person ID Filter*/}

                 {personIds[1] != "-" && <FormControl
                  sx={{
                    minWidth: 120,
                    "& .MuiInputLabel-root": { color: "#fff" },
                    "& .MuiSelect-select": {
                      color: "#fff",
                      background: "#252628",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                    "& .MuiSelect-icon": { color: "#fff" },
                  }}
                  size="small"
                >
                  <InputLabel>Person ID</InputLabel>
                  <Select
                    value={personIdFilter}
                    label="Person ID"
                    onChange={(e) => setPersonIdFilter(e.target.value)}
                  >
                    {personIds.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>}
                {/* Status Filter */}
                <FormControl
                  sx={{
                    minWidth: 120,
                    "& .MuiInputLabel-root": { color: "#fff" },
                    "& .MuiSelect-select": {
                      color: "#fff",
                      background: "#252628",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                    },
                    "& .MuiSelect-icon": { color: "#fff" },
                  }}
                  size="small"
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {status.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Clear Filters */}
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearFilters}
                  startIcon={<ClearOutlinedIcon />}
                >
                  Clear Filters
                </Button>
              </Box>

              {/* Search */}
              <TextField
                size="small"
                label="Search"
                variant="outlined"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  minWidth: 200,
                  "& label": { color: "#ccc" },
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#3C3B3B" },
                    "&:hover fieldset": { borderColor: "#3C3B3B" },
                  },
                  input: { color: "#fff" },
                }}
              />
            </Box>

            {/* Table */}
            <TableContainer sx={{ bgcolor: "#0F1116" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#1C1E22" }}>
                   
                      <TableCell
                          key="Pay scale type"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                          Pay scale type
                        </TableCell>
                        { personNo[1] !== "-" &&<TableCell
                          key="Person No."
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                          Person No.
                        </TableCell>}
                       { personIds[1] !== "-" && <TableCell
                          key="Person ID"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                          Person ID
                        </TableCell>}
                        <TableCell
                          key="Wage type"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                          Wage type
                        </TableCell>
                        <TableCell
                          key="Variance"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                          Variance
                        </TableCell>
                        <TableCell
                          key="Status"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          key="Rule number"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                         Rule number
                        </TableCell>
                        <TableCell
                          key="Rule description"
                          sx={{
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "14px",
                            borderBottom: "none",
                            borderRight: "1px solid #2F3337",
                          }}
                        >
                         Rule description
                        </TableCell>
                  
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedData.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: index % 2 === 0 ? "#26292E" : "#1C1E22",
                      }}
                    >
                      <TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>{row.payScaleType}</TableCell>
                      { personNo[1] !== "-" &&<TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>{row.personNo}</TableCell>}
                       { personIds[1] !== "-" &&<TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>{row.personId}</TableCell>}
                      <TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>{row.wageType}</TableCell>
                      <TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>{row.variance}</TableCell>
                      <TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>{row.status}</TableCell>
                      <TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>{row.ruleNumber}</TableCell>
                      <TableCell sx={{
                        color: "#BFC1C2",
                        borderBottom: "none",
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          {row.description}
                          <BootstrapTooltip
                            title={row.explanation || "No explanation available"}
                          >
                            <IconButton color="primary" size="small">
                              <img src="./icons/info.svg" alt="info" />
                            </IconButton>
                          </BootstrapTooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{
                        color: "#aaa",
                        borderBottom: "none",
                      }}>
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      sx={{
                        color: "#fff",
                        borderBottom: "none",
                        "& .MuiSelect-select, & .MuiIconButton-root, & .MuiTablePagination-displayedRows":
                          { color: "#fff" },
                      }}
                      rowsPerPageOptions={[5, 10, 25]}
                      count={filteredData.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Rows per page"
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        </div>
      </Box>
    </>
  );
};

export default ViewValidationResult;
